import type { WorkstreamAgent, Integration, IntegrationTemplate, PortcoDetail, OptimizationRecommendation, OnboardingStep, AppUser, AgentConfig, StatsSummary, AgentStatus } from '../types';

export const PORTCOS: PortcoDetail[] = [
  {
    id: 'portco_apex',
    name: 'Apex Healthcare Partners',
    code: 'AHCP',
    region: 'Midwest',
    status: 'active',
    ehrVendor: 'SETWorks',
    integrationCount: 9,
    agentCount: 8,
    complianceScore: 94,
    monthlyRevenue: '$2.4M',
    patients: 12400,
    staff: 340,
    facilities: 7,
    lastOnboardedAt: '2026-06-15T10:00:00Z',
    kpis: [
      { label: 'Patient Acquisition Cost', value: '$42.50', trend: 'down' },
      { label: 'Claim Denial Rate', value: '3.2%', trend: 'down' },
      { label: 'Days in AR', value: '22', trend: 'down' },
      { label: 'Staff Utilization', value: '87%', trend: 'up' },
      { label: 'Compliance Score', value: '94/100', trend: 'up' },
    ],
  },
  {
    id: 'portco_summit',
    name: 'Summit Behavioral Health',
    code: 'SBH',
    region: 'Northeast',
    status: 'active',
    ehrVendor: 'SETWorks',
    integrationCount: 9,
    agentCount: 8,
    complianceScore: 88,
    monthlyRevenue: '$1.8M',
    patients: 8200,
    staff: 210,
    facilities: 4,
    lastOnboardedAt: '2026-07-01T10:00:00Z',
    kpis: [
      { label: 'Referral Conversion', value: '28%', trend: 'up' },
      { label: 'Claim Denial Rate', value: '5.8%', trend: 'flat' },
      { label: 'Days in AR', value: '34', trend: 'up' },
      { label: 'Avg Wait Time', value: '5.1 days', trend: 'up' },
      { label: 'Compliance Score', value: '88/100', trend: 'up' },
    ],
  },
];

// Agent templates — 8 workstream agents per PortCo
type AgentTemplate = Omit<WorkstreamAgent, 'id' | 'portcoId'> & { type: string };

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    type: 'marketing',
    name: 'Campaign Optimizer',
    workstream: 'marketing',
    status: 'active',
    version: 'v2.4',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 22).toISOString(),
    actionsToday: 14,
    successRate: 92.8,
    description: 'Manages ad spend, campaign optimization, and patient acquisition across Google Ads and GoHighLevel.',
    capabilities: ['Budget Reallocation', 'A/B Testing', 'Audience Segmentation', 'Lead Scoring'],
    kpis: [
      { label: 'CPA', value: '$42.50', trend: 'down' },
      { label: 'ROAS', value: '4.2x', trend: 'up' },
    ],
    recentRecommendations: [
      { id: 'rec_mkt', title: 'Reallocate $12.5K to local intent keywords', impact: '+$48K revenue/mo', priority: 'medium', createdAt: new Date(Date.now() - 1000 * 60 * 22).toISOString() },
    ],
  },
  {
    type: 'referrals',
    name: 'Physician Liaison',
    workstream: 'referrals',
    status: 'processing',
    version: 'v1.8',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    actionsToday: 8,
    successRate: 96.2,
    description: 'Builds referral partner relationships, automates outreach, and tracks conversion.',
    capabilities: ['Partner Outreach', 'Referral Tracking', 'Conversion Analytics', 'Provider Scheduling'],
    kpis: [
      { label: 'Referral Volume', value: '342/mo', trend: 'up' },
      { label: 'Conversion Rate', value: '34%', trend: 'up' },
    ],
    recentRecommendations: [
      { id: 'rec_ref', title: 'Onboard Tri-County Orthopedics (18 MDs)', impact: '+31 referrals/mo', priority: 'low', createdAt: new Date(Date.now() - 1000 * 60 * 45).toISOString() },
    ],
  },
  {
    type: 'intake',
    name: 'Intake Coordinator',
    workstream: 'intake',
    status: 'active',
    version: 'v2.1',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 12).toISOString(),
    actionsToday: 31,
    successRate: 98.5,
    description: 'Automates patient intake, insurance verification, and appointment booking.',
    capabilities: ['Insurance Verification', 'Appointment Booking', 'Document Collection', 'Pre-Authorization'],
    kpis: [
      { label: 'Intake Cycle Time', value: '1.8 days', trend: 'down' },
      { label: 'Verification Accuracy', value: '98.5%', trend: 'up' },
    ],
    recentRecommendations: [
      { id: 'rec_int', title: 'Auto-verify insurance for 23 pending patients', impact: '-3 hrs manual work', priority: 'low', createdAt: new Date(Date.now() - 1000 * 60 * 12).toISOString() },
    ],
  },
  {
    type: 'scheduling',
    name: 'Schedule Optimizer',
    workstream: 'scheduling',
    status: 'active',
    version: 'v1.9',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    actionsToday: 26,
    successRate: 94.1,
    description: 'Optimizes provider schedules, manages cancellations, and fills no-show slots.',
    capabilities: ['Slot Optimization', 'Waitlist Management', 'Provider Load Balancing', 'Cancellation Recovery'],
    kpis: [
      { label: 'Slot Utilization', value: '87%', trend: 'up' },
      { label: 'Avg Wait Time', value: '3.2 days', trend: 'down' },
    ],
    recentRecommendations: [
      { id: 'rec_sch', title: 'Open Wednesday evening slots for Dr. Patel', impact: '+18 visits/wk', priority: 'medium', createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString() },
    ],
  },
  {
    type: 'rcm',
    name: 'Revenue Cycle Manager',
    workstream: 'rcm',
    status: 'processing',
    version: 'v3.0',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 3).toISOString(),
    actionsToday: 47,
    successRate: 95.7,
    description: 'Manages claims, denial analysis, payment posting, and AR aging.',
    capabilities: ['Claim Submission', 'Denial Analysis', 'Appeal Generation', 'Payment Reconciliation'],
    kpis: [
      { label: 'Claim Denial Rate', value: '4.2%', trend: 'down' },
      { label: 'Days in AR', value: '28', trend: 'down' },
    ],
    recentRecommendations: [
      { id: 'rec_rcm', title: 'Appeal 14 denied claims for missing prior-auth', impact: '+$87K recovery', priority: 'high', createdAt: new Date(Date.now() - 1000 * 60 * 3).toISOString() },
    ],
  },
  {
    type: 'hr',
    name: 'HR & Credentialing',
    workstream: 'hr',
    status: 'active',
    version: 'v1.6',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    actionsToday: 6,
    successRate: 99.1,
    description: 'Manages credentialing, license tracking, compliance training, and onboarding.',
    capabilities: ['License Tracking', 'Credential Verification', 'Compliance Training', 'Expiration Alerts'],
    kpis: [
      { label: 'Compliance Rate', value: '98.2%', trend: 'up' },
      { label: 'Expiring Credentials', value: '7', trend: 'down' },
    ],
    recentRecommendations: [
      { id: 'rec_hr', title: 'Renew 3 RN licenses expiring in 30 days', impact: 'Compliance risk', priority: 'high', createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString() },
    ],
  },
  {
    type: 'finance',
    name: 'Finance & Capital',
    workstream: 'finance',
    status: 'idle',
    version: 'v2.2',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
    actionsToday: 4,
    successRate: 97.8,
    description: 'Tracks financial performance, AP/AR, PE reporting, and working capital.',
    capabilities: ['Financial Reporting', 'AP/AR Automation', 'PE Dashboard Generation', 'Cash Flow Analysis'],
    kpis: [
      { label: 'Cash Position', value: '$4.2M', trend: 'up' },
      { label: 'EBITDA Margin', value: '23%', trend: 'up' },
    ],
    recentRecommendations: [
      { id: 'rec_fin', title: 'Consolidate vendor contracts for 3 facilities', impact: '+$180K savings/yr', priority: 'medium', createdAt: new Date(Date.now() - 1000 * 60 * 120).toISOString() },
    ],
  },
  {
    type: 'clinical-ops',
    name: 'Clinical Ops',
    workstream: 'clinical-ops',
    status: 'active',
    version: 'v2.0',
    lastActionAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    actionsToday: 19,
    successRate: 98.3,
    description: 'Monitors clinical quality, patient outcomes, care plan adherence, and compliance.',
    capabilities: ['Quality Metrics', 'Outcome Tracking', 'Care Plan Audits', 'Regulatory Compliance'],
    kpis: [
      { label: 'Quality Score', value: '4.7/5', trend: 'up' },
      { label: 'Care Plan Adherence', value: '93%', trend: 'up' },
    ],
    recentRecommendations: [
      { id: 'rec_clin', title: 'Update 2 care plans per latest clinical guidelines', impact: 'Compliance', priority: 'high', createdAt: new Date(Date.now() - 1000 * 60 * 15).toISOString() },
    ],
  },
];

// Per-PortCo stat variations — genuinely different data per portco
const PORTCO_VARIATIONS: Record<string, {
  actionsMultiplier: number;
  successDelta: number;
  statusOverrides: Partial<Record<string, AgentStatus>>;
}> = {
  portco_apex: {
    actionsMultiplier: 1.0,
    successDelta: 0,
    statusOverrides: { finance: 'active' },
  },
  portco_summit: {
    actionsMultiplier: 0.55,
    successDelta: -3.5,
    statusOverrides: {
      finance: 'idle',
      'clinical-ops': 'processing',
      marketing: 'idle',
      referrals: 'idle',
    },
  },
};

// Instantiate agents for each PortCo with differentiated stats
export const AGENTS: WorkstreamAgent[] = PORTCOS.flatMap((portco) => {
  const variation = PORTCO_VARIATIONS[portco.id];
  return AGENT_TEMPLATES.map((tmpl) => {
    const isApex = portco.id === 'portco_apex';
    const override = variation.statusOverrides?.[tmpl.type];

    return {
      ...tmpl,
      id: `agent_${tmpl.type}_${portco.id}`,
      portcoId: portco.id,
      actionsToday: Math.round((tmpl.actionsToday || 0) * variation.actionsMultiplier),
      successRate: Math.round(((tmpl.successRate || 95) + variation.successDelta) * 10) / 10,
      status: override || tmpl.status,
      lastActionAt: new Date(Date.now() - Math.random() * 1000 * 60 * 60 * 2).toISOString(),
      recentRecommendations: tmpl.recentRecommendations.map((r) => ({
        ...r,
        id: `${r.id}_${portco.id}`,
      })),
      kpis: tmpl.kpis.map(k => {
        if (isApex) return k;
        if (k.label === 'Claim Denial Rate') return { ...k, value: '5.8%', trend: 'flat' as const };
        if (k.label === 'Days in AR') return { ...k, value: '34', trend: 'up' as const };
        if (k.label === 'Slot Utilization') return { ...k, value: '78%', trend: 'up' as const };
        if (k.label === 'Avg Wait Time') return { ...k, value: '5.1 days', trend: 'up' as const };
        if (k.label === 'Referral Volume') return { ...k, value: '156/mo', trend: 'up' as const };
        if (k.label === 'Conversion Rate') return { ...k, value: '28%', trend: 'up' as const };
        if (k.label === 'CPA') return { ...k, value: '$58.20', trend: 'flat' as const };
        if (k.label === 'ROAS') return { ...k, value: '3.1x', trend: 'up' as const };
        return k;
      }),
    };
  });
});

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-TENANT INTEGRATION MODEL
// Templates = catalog of connectable vendor adapters (shared across portcos).
// PORTCO_INTEGRATIONS = per-business connection instances, each with its OWN
// tenant credentials, config values, sync state, and health score.
// ─────────────────────────────────────────────────────────────────────────────

const mins = (n: number) => new Date(Date.now() - 1000 * 60 * n).toISOString();
const hrs = (n: number) => new Date(Date.now() - 1000 * 60 * 60 * n).toISOString();

export const INTEGRATION_TEMPLATES: IntegrationTemplate[] = [
  {
    templateId: 'tpl_setworks',
    name: 'SETWorks EHR',
    category: 'EHR',
    vendor: 'SETWorks',
    description: 'EHR and IDD case management integration. CSV exchange via Azure Storage with browser automation fallback.',
    configFields: [
      { key: 'baseUrl', label: 'SETWorks URL', type: 'text', required: true },
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'integrationMode', label: 'Integration Mode', type: 'select', required: true, options: ['CSV', 'Browser Automation', 'Private API (Future)'] },
      { key: 'storageAccount', label: 'Azure Storage Account', type: 'text', required: true },
      { key: 'csvContainer', label: 'CSV Container', type: 'text', required: true },
    ],
  },
  {
    templateId: 'tpl_quickbooks',
    name: 'QuickBooks Online',
    category: 'Finance',
    vendor: 'Intuit',
    description: 'Financial data sync for AP/AR, expense tracking, and PE reporting. OAuth 2.0 with automatic token refresh.',
    configFields: [
      { key: 'companyId', label: 'Company ID', type: 'text', required: true },
      { key: 'clientId', label: 'OAuth Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'OAuth Client Secret', type: 'password', required: true },
      { key: 'refreshToken', label: 'Refresh Token', type: 'password', required: true },
      { key: 'environment', label: 'Environment', type: 'select', required: true, options: ['production', 'sandbox'] },
    ],
  },
  {
    templateId: 'tpl_bamboohr',
    name: 'BambooHR',
    category: 'HR',
    vendor: 'BambooHR',
    description: 'HRIS integration for employee records, credentialing, license tracking, and compliance training.',
    configFields: [
      { key: 'subdomain', label: 'Subdomain', type: 'text', required: true },
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'autoSync', label: 'Auto-Sync Interval', type: 'select', required: true, options: ['5', '15', '30', '60'] },
    ],
  },
  {
    templateId: 'tpl_gohighlevel',
    name: 'GoHighLevel',
    category: 'Marketing',
    vendor: 'HighLevel',
    description: 'CRM and marketing automation for patient acquisition, lead scoring, and campaign management.',
    configFields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
      { key: 'locationId', label: 'Location ID', type: 'text', required: true },
      { key: 'webhookUrl', label: 'Webhook URL', type: 'url', required: false },
    ],
  },
  {
    templateId: 'tpl_googleads',
    name: 'Google Ads',
    category: 'Marketing',
    vendor: 'Google',
    description: 'Paid search and display ad management for patient acquisition campaigns. OAuth 2.0 with refresh token.',
    configFields: [
      { key: 'clientId', label: 'OAuth Client ID', type: 'text', required: true },
      { key: 'clientSecret', label: 'OAuth Client Secret', type: 'password', required: true },
      { key: 'refreshToken', label: 'Refresh Token', type: 'password', required: true },
      { key: 'customerId', label: 'Customer ID', type: 'text', required: true },
    ],
  },
  {
    templateId: 'tpl_excels',
    name: 'Excels Intake Forms',
    category: 'Intake',
    vendor: 'Excels',
    description: 'Patient intake data collection via Excel spreadsheets. Automated parsing, validation, and EHR upload via CSV exchange.',
    configFields: [
      { key: 'storageAccount', label: 'Azure Storage Account', type: 'text', required: true },
      { key: 'container', label: 'Container Name', type: 'text', required: true },
      { key: 'templateVersion', label: 'Template Version', type: 'text', required: true },
      { key: 'autoParse', label: 'Auto-Parse on Upload', type: 'select', required: true, options: ['true', 'false'] },
      { key: 'validationRules', label: 'Validation Ruleset', type: 'select', required: true, options: ['idd-standard', 'behavioral-health', 'general'] },
    ],
  },
];

const MASK = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';

// Build a per-portco instance of a template with tenant-specific config values.
function instance(
  templateId: string,
  portcoId: string,
  overrides: {
    status: Integration['status'];
    healthScore: number;
    dataPoints: number;
    lastSync: string;
    config: Record<string, string>;
  }
): Integration {
  const tpl = INTEGRATION_TEMPLATES.find((t) => t.templateId === templateId)!;
  return {
    id: `${templateId}_${portcoId}`,
    templateId,
    portcoId,
    name: tpl.name,
    category: tpl.category,
    vendor: tpl.vendor,
    description: tpl.description,
    status: overrides.status,
    healthScore: overrides.healthScore,
    dataPoints: overrides.dataPoints,
    lastSync: overrides.lastSync,
    configFields: tpl.configFields.map((f) => ({
      ...f,
      value: overrides.config[f.key] ?? MASK,
    })),
  };
}

// Per-portco integration instances — each business connects its OWN tenant.
export const PORTCO_INTEGRATIONS: Record<string, Integration[]> = {
  portco_apex: [
    instance('tpl_setworks', 'portco_apex', {
      status: 'connected', healthScore: 92, dataPoints: 14820, lastSync: mins(15),
      config: {
        baseUrl: 'https://app.setworks.com',
        integrationMode: 'csv',
        storageAccount: 'meridiandevstorage',
        csvContainer: 'setworks-exchange-apex',
      },
    }),
    instance('tpl_quickbooks', 'portco_apex', {
      status: 'connected', healthScore: 88, dataPoints: 8420, lastSync: mins(45),
      config: { companyId: '123145872930', clientId: 'AB1234567890', environment: 'production' },
    }),
    instance('tpl_bamboohr', 'portco_apex', {
      status: 'connected', healthScore: 95, dataPoints: 3200, lastSync: mins(30),
      config: { subdomain: 'apexhealth', autoSync: '15' },
    }),
    instance('tpl_gohighlevel', 'portco_apex', {
      status: 'connected', healthScore: 90, dataPoints: 12650, lastSync: mins(10),
      config: {
        locationId: 'loc_apex_001',
        webhookUrl: 'https://api.meridianhealthops.com/api/webhooks/gohighlevel/apex',
      },
    }),
    instance('tpl_googleads', 'portco_apex', {
      status: 'connected', healthScore: 87, dataPoints: 5400, lastSync: hrs(1),
      config: { clientId: '123456789-abc.apps.googleusercontent.com', customerId: '123-456-7890' },
    }),
    instance('tpl_excels', 'portco_apex', {
      status: 'connected', healthScore: 85, dataPoints: 6800, lastSync: mins(20),
      config: { storageAccount: 'meridiandevstorage', container: 'intake-forms-apex', templateVersion: 'v2.1', autoParse: 'true', validationRules: 'idd-standard' },
    }),
  ],
  portco_summit: [
    instance('tpl_setworks', 'portco_summit', {
      status: 'connected', healthScore: 78, dataPoints: 9310, lastSync: mins(52),
      config: {
        baseUrl: 'https://app.setworks.com',
        integrationMode: 'csv',
        storageAccount: 'meridiandevstorage',
        csvContainer: 'setworks-exchange-summit',
      },
    }),
    instance('tpl_quickbooks', 'portco_summit', {
      status: 'connected', healthScore: 84, dataPoints: 5120, lastSync: hrs(2),
      config: { companyId: '987654321045', clientId: 'CD0987654321', environment: 'production' },
    }),
    instance('tpl_bamboohr', 'portco_summit', {
      status: 'pending', healthScore: 0, dataPoints: 0, lastSync: '\u2014',
      config: { subdomain: 'summitbh', autoSync: '30' },
    }),
    instance('tpl_gohighlevel', 'portco_summit', {
      status: 'error', healthScore: 41, dataPoints: 2140, lastSync: hrs(26),
      config: {
        locationId: 'loc_summit_002',
        webhookUrl: 'https://api.meridianhealthops.com/api/webhooks/gohighlevel/summit',
      },
    }),
    instance('tpl_googleads', 'portco_summit', {
      status: 'connected', healthScore: 82, dataPoints: 2980, lastSync: hrs(3),
      config: { clientId: '456789123-def.apps.googleusercontent.com', customerId: '987-654-3210' },
    }),
    instance('tpl_excels', 'portco_summit', {
      status: 'disconnected', healthScore: 0, dataPoints: 0, lastSync: '\u2014',
      config: { storageAccount: 'meridiandevstorage', container: 'intake-forms-summit', templateVersion: 'v2.1', autoParse: 'true', validationRules: 'behavioral-health' },
    }),
  ],
};

// All instances across every portco (flat view for aggregation).
export const INTEGRATIONS: Integration[] = Object.values(PORTCO_INTEGRATIONS).flat();

// Get a portco's integration instances (or all when 'all').
export function getIntegrationsForPortco(portcoId: string | 'all'): Integration[] {
  if (portcoId === 'all') return INTEGRATIONS;
  return PORTCO_INTEGRATIONS[portcoId] ?? [];
}

// Map workstream -> template ids it depends on (tenant-agnostic).
export const WORKSTREAM_INTEGRATIONS: Record<string, string[]> = {
  'rcm': ['tpl_setworks', 'tpl_quickbooks'],
  'hr': ['tpl_bamboohr'],
  'marketing': ['tpl_gohighlevel', 'tpl_googleads'],
  'intake': ['tpl_excels', 'tpl_setworks'],
  'finance': ['tpl_quickbooks'],
  'scheduling': ['tpl_setworks'],
  'referrals': ['tpl_gohighlevel', 'tpl_setworks'],
  'clinical-ops': ['tpl_setworks'],
};

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    id: 'step_1',
    title: 'PortCo Profile',
    description: 'Basic business info and PortCo details',
    completed: true,
    fields: [
      { key: 'companyName', label: 'Legal Entity Name', type: 'text', required: true, placeholder: 'Apex Healthcare Partners LLC' },
      { key: 'portcoCode', label: 'PortCo Code', type: 'text', required: true, placeholder: 'AHCP' },
      { key: 'region', label: 'Region', type: 'select', required: true, placeholder: 'Midwest', options: ['Midwest', 'Northeast', 'South', 'West'] },
    ],
  },
  {
    id: 'step_2',
    title: 'EHR Integration',
    description: 'Connect to SETWorks or other EHR systems',
    completed: true,
    fields: [
      { key: 'ehrVendor', label: 'EHR Vendor', type: 'select', required: true, placeholder: 'SETWorks', options: ['SETWorks', 'Other'] },
      { key: 'ehrUrl', label: 'EHR URL', type: 'url', required: true, placeholder: 'https://app.setworks.com' },
      { key: 'apiKey', label: 'API Key', type: 'password', required: true, placeholder: '••••••••' },
      { key: 'integrationMode', label: 'Integration Mode', type: 'select', required: true, placeholder: 'CSV', options: ['CSV', 'Browser Automation'] },
    ],
  },
  {
    id: 'step_3',
    title: 'Agent Configuration',
    description: 'Select and configure autonomous agents',
    completed: true,
    fields: [
      { key: 'agents', label: 'Agents to Enable', type: 'toggle', required: true, placeholder: 'Select agents' },
      { key: 'riskThreshold', label: 'Risk Threshold', type: 'select', required: true, placeholder: 'Medium', options: ['Low', 'Medium', 'High'] },
      { key: 'autoApprove', label: 'Auto-approve Low Risk', type: 'toggle', required: false, placeholder: 'false' },
    ],
  },
  {
    id: 'step_4',
    title: 'Compliance & Security',
    description: 'Configure HIPAA, CMK, and data isolation',
    completed: true,
    fields: [
      { key: 'hipaa', label: 'HIPAA Compliance', type: 'toggle', required: true, placeholder: 'true' },
      { key: 'cmk', label: 'Customer-Managed Keys', type: 'toggle', required: true, placeholder: 'true' },
      { key: 'dataRetention', label: 'Data Retention Policy', type: 'select', required: true, placeholder: 'Zero retention', options: ['Zero retention', '30 days', '90 days'] },
    ],
  },
  {
    id: 'step_5',
    title: 'Go Live',
    description: 'Activate agents and start processing',
    completed: false,
    fields: [
      { key: 'confirm', label: 'Confirm Deployment', type: 'toggle', required: true, placeholder: 'false' },
    ],
  },
];

export const APP_USERS: AppUser[] = [
  { id: 'user_1', name: 'Sarah Chen', email: 'sarah.chen@freesolo.capital', role: 'owner', portcoIds: ['portco_apex', 'portco_summit'], workstreams: ['rcm', 'marketing', 'hr', 'finance', 'intake', 'scheduling', 'referrals', 'clinical-ops'], status: 'active', lastActive: 'Now' },
  { id: 'user_2', name: 'Michael Torres', email: 'm.torres@apexhealth.com', role: 'admin', portcoIds: ['portco_apex'], workstreams: ['rcm', 'intake', 'scheduling'], status: 'active', lastActive: '2h ago' },
  { id: 'user_3', name: 'Dr. Amanda Reyes', email: 'a.reyes@summitbehavioral.com', role: 'reviewer', portcoIds: ['portco_summit'], workstreams: ['clinical-ops', 'rcm'], status: 'active', lastActive: '1d ago' },
];

export const USERS = APP_USERS;

export const OPTIMIZATION_RECOMMENDATIONS: OptimizationRecommendation[] = [
  { id: 'opt_1', title: 'Consolidate billing across 3 facilities', description: 'Merge billing operations for 3 Apex facilities into a shared revenue cycle center, reducing overhead and standardizing denial management.', workstream: 'rcm', portcoId: 'portco_apex', estimatedImpact: '$180K/yr savings', priority: 'high', status: 'new', createdAt: new Date(Date.now() - 86400000).toISOString(), actionItems: ['Audit current billing operations', 'Design shared service center', 'Migrate 3 facilities to consolidated workflow'] },
  { id: 'opt_2', title: 'Expand telehealth scheduling capacity', description: 'Add evening telehealth slots at Summit to address 5.1 day average wait time and capture overflow demand.', workstream: 'scheduling', portcoId: 'portco_summit', estimatedImpact: '$95K/yr revenue', priority: 'medium', status: 'new', createdAt: new Date(Date.now() - 86400000 * 2).toISOString(), actionItems: ['Assess provider availability for evening slots', 'Update scheduling templates', 'Notify patients of new availability'] },
];
